import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import isEmpty from 'lodash.isempty';
import { useStateMachine } from 'little-state-machine';
import countryList from 'country-list/data.json';

import passwordValidator from 'utils/passwordValidator';
import emailValidator from 'utils/emailValidator';

import { getCSRFToken } from 'api/signup';
import { FormDataProps, FormFieldType } from './types';
import { Actions } from './Actions';
import { Wrap, FormContainer, Form, InputStyled, LabelStyled, BaseTitle } from './styled';
import { PasswordInput } from './FormFields/PasswordInput';
import { TosField } from './FormFields/TosField';
import { useLocation } from 'react-router-dom';
import { Select } from 'Components';
import { countryOptions } from '../PaymentDetails/PaymentForm';
import { trackEvent } from 'v6/api/signup';
import { Alert } from 'v6/Components/Alert';
import styled from 'styled-components';
import { SocialProof } from './SocialProof';

const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

const generateFormActionProps = ({ trigger, resetField, getValues }: any, name: string) => {
  return {
    onBlur: () => trigger(name),
    onFocus: () => resetField(name, { defaultValue: getValues(name) }),
  };
};
const Homepage = () => {
  const { state } = useStateMachine();
  const defaultValuesFromStorage = state as FormDataProps;
  // on initial load, if query string is present e.g. ?email=hugh@skedsocial.com - pre-fill this info based on the types
  const query = useQuery();
  [
    'email',
    'firstName',
    'lastName',
    'companyName',
    'selectedPlanId',
    'currency',
    'numberAccounts',
    'cbSubId',
  ].forEach(key => {
    // @ts-ignore
    if (!!query.get(key)) {
      // @ts-ignore
      defaultValuesFromStorage[key] = query.get(key);
    }
  });

  const form = useForm<FormDataProps>({ mode: 'onSubmit' });
  const { control, handleSubmit, formState, trigger, getValues, watch } = form;
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  const { errors, isValid: isValidForm } = formState;
  const watchAllFields = watch();
  const handleChange = (e: ChangeEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    onChange(e.target.value?.trim() ? e.target.value : '');
  };

  useEffect(() => {
    getCSRFToken();

    //validate filled email if any
    if (!isEmpty(defaultValuesFromStorage)) {
      trigger('email');
    }

    trackEvent({
      event: 'sign_up-create_login-page-viewed',
    });

    trackEvent({
      event: 'sign-up-24-step-1',
    });

    /**
     * TODO to always trigger credit card, ignore "?no-cc" from path
     */
    sessionStorage.removeItem('skipCreditCardDetail');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formFields: FormFieldType[] = [
    {
      name: 'firstName',
      label: 'First Name',
      defaultValue: defaultValuesFromStorage.firstName || '',
      rules: {
        required: 'First name is required.',
        maxLength: 50,
      },
      render: ({ field: { value, onChange } }: any) => {
        let errorMsg = errors.firstName?.message;

        if (errors.firstName?.type === 'maxLength') {
          errorMsg = 'First name must be less than 50 characters.';
        }

        const handleFirstNameChange = (e: ChangeEvent<HTMLInputElement>) => {
          handleChange(e, onChange);
        };

        return (
          <InputStyled
            data-testid="firstNameInput"
            id="firstName"
            type="text"
            autoComplete="given-name"
            className="data-hj-allow"
            placeholder="First name"
            value={value}
            onChange={handleFirstNameChange}
            error={errorMsg}
            {...generateFormActionProps(form, 'firstName')}
          />
        );
      },
    },
    {
      name: 'lastName',
      label: 'Last Name',
      defaultValue: defaultValuesFromStorage.lastName || '',
      rules: {
        required: 'Last name is required.',
        maxLength: 50,
      },
      render: ({ field: { value, onChange } }: any) => {
        let errorMsg = errors.lastName?.message;

        if (errors.lastName?.type === 'maxLength') {
          errorMsg = 'Last name must be less than 50 characters.';
        }

        const handleLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
          handleChange(e, onChange);
        };

        return (
          <InputStyled
            data-testid="lastNameInput"
            id="lastName"
            autoComplete="family-name"
            type="text"
            className="data-hj-allow"
            placeholder="Last name"
            value={value}
            onChange={handleLastNameChange}
            error={errorMsg}
            {...generateFormActionProps(form, 'lastName')}
          />
        );
      },
    },
    {
      name: 'companyName',
      label: 'Company Name',
      defaultValue: defaultValuesFromStorage.companyName || '',
      rules: {
        required: 'Company Name is required.',
        maxLength: 50,
      },
      render: ({ field: { value, onChange } }: any) => {
        let errorMsg = errors.companyName?.message;

        if (errors.companyName?.type === 'maxLength') {
          errorMsg = 'Company name must be less than 50 characters.';
        }

        const handleCompanyNameChange = (e: ChangeEvent<HTMLInputElement>) => {
          handleChange(e, onChange);
        };

        return (
          <InputStyled
            data-testid="companyNameInput"
            id="companyName"
            className="data-hj-allow"
            autoComplete="organization"
            placeholder="Company Name"
            type="text"
            value={value}
            onChange={handleCompanyNameChange}
            error={errorMsg}
            {...generateFormActionProps(form, 'companyName')}
          />
        );
      },
    },
    {
      name: 'email',
      label: 'Email',
      defaultValue: defaultValuesFromStorage.email || '',
      rules: {
        required: 'Email is required.',
        pattern: {
          value:
            // eslint-disable-next-line no-useless-escape
            /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/,
          message: 'Please enter a valid email address.',
        },
      },
      render: ({ field: { value, onChange } }: any) => (
        <>
          <div>
            <InputStyled
              data-testid="emailInput"
              id="email"
              className="data-hj-allow"
              placeholder="Email"
              autoComplete="email"
              type="text"
              value={value}
              onChange={onChange}
              error={errors.email?.message}
              warning={emailValidator(value)}
              maxLength={50}
              {...generateFormActionProps(form, 'email')}
            />
          </div>
        </>
      ),
    },
    {
      name: 'password',
      label: 'Password',
      defaultValue: defaultValuesFromStorage.password || '',
      rules: {
        required: 'Password is required.',
        minLength: 8,
        maxLength: 50,
        validate: (value: string) =>
          passwordValidator(value)
            .map(rule => rule.valid)
            .filter(Boolean).length >= 3, //looks looks redundant because have to put error into errors object if any
      },
      render: ({ field: { value, onChange } }: any) => {
        let errorMsg;

        if (errors.password?.type === 'required' && errors.password?.message) {
          errorMsg = errors.password?.message;
        } else {
          switch (errors.password?.type) {
            case 'minLength':
              errorMsg = 'Password must be at least 8 characters.';
              break;
            case 'maxLength':
              errorMsg = 'Password must be less than 25 characters.';
              break;
            case 'validate':
              errorMsg =
                'Password needs to include a combination of letters, numbers and special characters.';
              break;
          }
        }

        return (
          <PasswordInput
            errorMsg={errorMsg}
            value={value}
            onChange={onChange}
            errors={errors}
            defaultValue={defaultValuesFromStorage.password}
            {...generateFormActionProps(form, 'password')}
            onBlur={() => {
              trigger('password');
              const confirmPasswordValue = getValues('confirmPassword');

              if (confirmPasswordValue) {
                trigger('confirmPassword');
              }
            }}
          />
        );
      },
    },
    {
      name: 'country',
      label: 'Country',
      defaultValue: defaultValuesFromStorage.country || '',
      rules: {
        required: 'This field is required.',
      },
      render: ({ field: { value, onChange } }: any) => {
        let errorMsg = errors.country?.message;

        const handleCountyChange = ({ value }: { value: string }) => {
          handleChange({ target: { value } } as ChangeEvent<HTMLInputElement>, onChange);
        };

        const record = countryList.find(item => item.code === defaultValuesFromStorage.country);

        const defaultValue = record
          ? {
              value: record.code,
              label: record.name,
            }
          : undefined;

        return (
          <div style={{ marginTop: '8px' }}>
            <Select
              isSearchable
              name="country"
              data-testid="countryInput"
              className="data-hj-allow"
              options={countryOptions}
              isCustomValue={false}
              placeholder="Country"
              defaultValue={defaultValue}
              value={value}
              onChange={handleCountyChange}
              error={errorMsg}
              {...generateFormActionProps(form, 'country')}
            />
          </div>
        );
      },
    },

    {
      name: 'numberAccounts',
      label: 'How many accounts do you manage?',
      defaultValue: defaultValuesFromStorage.numberAccounts || '',
      rules: {
        required: 'This field is required.',
        min: 1,
        max: 250,
      },
      render: ({ field: { value, onChange } }: any) => {
        let errorMsg = '';

        if (errors.numberAccounts?.type === 'max') {
          errorMsg = 'Please contact sales for more than 250 accounts.';
        } else if (errors.numberAccounts) {
          errorMsg = 'Accounts number is not correct';
        }

        const handleNumberAccountsChange = (e: ChangeEvent<HTMLInputElement>) => {
          handleChange(e, onChange);
        };

        return (
          <InputStyled
            data-testid="numberAccountsInput"
            id="numberAccounts"
            className="data-hj-allow"
            placeholder="# of accounts"
            type="number"
            min="1"
            max="250"
            value={value}
            onChange={handleNumberAccountsChange}
            error={errors.numberAccounts?.message || errorMsg}
            {...generateFormActionProps(form, 'numberAccounts')}
          />
        );
      },
    },
    {
      name: 'tos',
      label: '',
      defaultValue: defaultValuesFromStorage.tos || false,
      rules: {
        required: 'Please check to agree to the terms of service and privacy policy.',
      },
      render: ({ field: { value, onChange } }: any) => (
        <TosField value={value} onChange={onChange} error={errors.tos?.message} />
      ),
    },
  ];

  return (
    <Split>
      <SocialProof />
      <FormColumn className="split-column">
        <Wrap>
          <FormContainer>
            <StepTitle>Please enter your details</StepTitle>

            {serverErrorMessage && (
              <AlertContainer>
                <Alert type="error">{serverErrorMessage}</Alert>
              </AlertContainer>
            )}
            <Form>
              {formFields
                .filter(({ name }) => name !== 'tos')
                .map(({ name, label, defaultValue, rules, render }) => (
                  <LabelStyled key={name} htmlFor={name}>
                    {label}
                    <Controller
                      control={control}
                      name={name}
                      defaultValue={defaultValue}
                      rules={rules}
                      render={render}
                    />
                  </LabelStyled>
                ))}

              <div style={{ display: 'flex' }}>
                {formFields
                  .filter(({ name }) => name === 'tos')
                  .map(({ name, defaultValue, rules, render }) => (
                    <Controller
                      key={name}
                      control={control}
                      name={name}
                      defaultValue={defaultValue}
                      rules={rules}
                      render={render}
                    />
                  ))}
              </div>
              <Actions
                isValidForm={isValidForm}
                dirtyFields={watchAllFields}
                handleSubmit={handleSubmit}
                setError={setServerErrorMessage}
                hasCheckedTos={getValues('tos')}
              />
            </Form>
          </FormContainer>
        </Wrap>
      </FormColumn>
    </Split>
  );
};

const StepTitle = styled(BaseTitle)`
  text-align: left;
`;

const AlertContainer = styled.div`
  margin-top: 20px;
`;

const Split = styled.div`
  display: flex;
  flex-direction: row;

  .split-column {
    flex: 1;
    align-items: stretch;
    justify-content: stretch;
  }
`;

const FormColumn = styled.div`
  display: flex;
  padding-bottom: 24px;
`;

export default Homepage;
